import React, { useEffect, useState, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import { Line } from "rc-progress";

import BasicInfoCard from "../../components/Cards/BasicInfoCard";
import ActionButton from "../../components/Buttons/ActionButton";
import { ReactComponent as TuneIcon } from "../../assets/icons/tune.svg";
import { ReactComponent as WorkerIcon } from "../../assets/icons/worker.svg";

import {
  useDevice,
  useDeviceGroup,
  useDevices,
  useMetrics,
  useWorkers,
} from "../../Api/newSockets.api";
import { useAuth } from "../../context/AuthContext";
import { devicesApi } from "../../Api/devices.api";
import { DeviceTypeId } from "../../constants/deviceTypes";
import { savePathToLocalStorage } from "../../utils/helpers";
import { isDeviceSocketLive } from "../../utils/deviceSocket";
import BakeryProcessCanvas from "./BakeryProcessCanvas";
import CapturePreviewModal from "../../components/Modals/CapturePreviewModal";
import NetworkGraph from "./NetworkGraph";
import { Factory3DViewer } from "../Factory";

// New componentized imports
import { DashboardGroupCard } from "./components";
import { useDeviceHierarchy } from "./hooks";

type DashboardView = "metrics" | "process" | "network" | "factory";

const Dashboard = () => {
  savePathToLocalStorage();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();

  // Get initial view from URL query param or default to "process"
  const getInitialView = (): DashboardView => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam === "metrics" || tabParam === "process" || tabParam === "network" || tabParam === "factory") {
      return tabParam;
    }
    return "process"; // Default to process
  };

  const [view, setView] = useState<DashboardView>(getInitialView);

  // Update URL when view changes
  const handleViewChange = (newView: DashboardView) => {
    setView(newView);
    const params = new URLSearchParams(location.search);
    params.set("tab", newView);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  // Generic metrics state: stores metrics per device
  const [deviceMetrics, setDeviceMetrics] = useState<Record<number, Record<string, any[]>>>(() => {
    try {
      const savedMetrics = localStorage.getItem('deviceMetrics');
      if (savedMetrics) {
        return JSON.parse(savedMetrics);
      }
    } catch (error) {
      console.error('Failed to load persisted metrics:', error);
    }
    return {};
  });

  // Modal state for Mixer Run procedure
  const [mixerModalOpen, setMixerModalOpen] = useState(false);
  const [mixerFormData, setMixerFormData] = useState({
    temperature: 25,
    speed: 500,
    duration: 60,
    direction: 'clockwise',
  });

  // Modal state for QA Capture
  const [captureModalOpen, setCaptureModalOpen] = useState(false);
  const [captureName, setCaptureName] = useState('');
  const [captureDeviceId, setCaptureDeviceId] = useState<number | null>(null);
  const [captureLoading, setCaptureLoading] = useState(false);

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [capturedProcessedImageData, setCapturedProcessedImageData] = useState('');
  const [capturedOriginalImageData, setCapturedOriginalImageData] = useState('');
  const [capturedMetrics, setCapturedMetrics] = useState<any>({});
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { devices } = useDevices();
  const { cameraCapture, confirmCapture } = useDevice();
  const { getDeviceGroup } = useDeviceGroup();
  const { workers } = useWorkers();
  const { metrics } = useMetrics();

  const metricsArray = useMemo(() => {
    return metrics ? Array.isArray(metrics) ? metrics : [metrics] : [];
  }, [metrics]);

  // Process incoming metrics
  useEffect(() => {
    if (!metricsArray || metricsArray.length === 0) return;

    metricsArray.forEach((metric: any) => {
      console.log(`Received metrics from device ${metric.deviceId}:`, metric);
    });

    setDeviceMetrics((prevDeviceMetrics) => {
      const updatedDeviceMetrics: Record<number, Record<string, any[]>> = { ...prevDeviceMetrics };
      const deviceIds = Array.from(new Set(metricsArray.map((m: any) => m.deviceId)));

      deviceIds.forEach((deviceId: any) => {
        const deviceMetricsList = metricsArray.filter((m: any) => m.deviceId === deviceId);
        const existingMetrics = prevDeviceMetrics[deviceId] || {};
        const metricKeys = new Set<string>();

        deviceMetricsList.forEach((met: any) => {
          Object.keys(met).forEach((key) => {
            if (key !== 'deviceId' && key !== 'organizationId' && (typeof met[key] === 'number' || key === 'state')) {
              metricKeys.add(key);
            }
          });
        });

        const metricData: Record<string, any[]> = {};
        metricKeys.forEach((key) => {
          // For power metrics, accumulate all data points
          if ((key === 'power' || key === 'power_reconstruction') &&
              Object.keys(existingMetrics).some(k => ['power', 'power_reconstruction'].includes(k))) {
            metricData[key] = existingMetrics[key] ? [...existingMetrics[key]] : [];
          } else if (key === 'state') {
            metricData[key] = existingMetrics[key]
              ? [...existingMetrics[key]]
              : new Array(12).fill('Idle');
          } else {
            metricData[key] = existingMetrics[key]
              ? [...existingMetrics[key]]
              : new Array(12).fill(0);
          }
        });

        deviceMetricsList.forEach((met: any) => {
          metricKeys.forEach((key) => {
            if ((key === 'power' || key === 'power_reconstruction') &&
                Object.keys(existingMetrics).some(k => ['power', 'power_reconstruction'].includes(k))) {
              metricData[key].push(met[key] ?? 0);
            } else if (key === 'state') {
              metricData[key].shift();
              metricData[key].push(met[key] ?? 'Idle');
            } else {
              metricData[key].shift();
              metricData[key].push(met[key] ?? 0);
            }
          });
        });

        updatedDeviceMetrics[deviceId] = metricData;
      });

      return updatedDeviceMetrics;
    });
  }, [metricsArray]);

  // Persist deviceMetrics to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('deviceMetrics', JSON.stringify(deviceMetrics));
    } catch (error) {
      console.error('Failed to persist metrics:', error);
    }
  }, [deviceMetrics]);

  // Device filtering
  const onlineDevices = devices?.filter(
    (d) => isDeviceSocketLive(d) && d.deviceTypes?.id === DeviceTypeId.HOST
  );

  const offlineDevices = devices?.filter(
    (d) => !isDeviceSocketLive(d) && d.deviceTypes?.id === DeviceTypeId.HOST
  );

  // Use the new hook for device hierarchy
  const { hostDevices, devicesByHost } = useDeviceHierarchy(devices);

  // Track which devices we've already loaded historical data for
  const loadedHistoricalRef = useRef<Set<number>>(new Set());

  // Load historical metrics for PLC/USB devices (last hour)
  useEffect(() => {
    if (!devices || devices.length === 0) return;

    const plcUsbDevices = devices.filter(
      (d: any) => d.deviceTypes?.id === DeviceTypeId.PLC || d.deviceTypes?.id === DeviceTypeId.USB
    );

    plcUsbDevices.forEach(async (device: any) => {
      // Skip if already loaded
      if (loadedHistoricalRef.current.has(device.id)) return;
      loadedHistoricalRef.current.add(device.id);

      try {
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const historicalMetrics = await devicesApi.getDeviceMetrics(device.id, {
          from: oneHourAgo,
        });

        if (!historicalMetrics || historicalMetrics.length === 0) return;

        // Transform historical data into the format used by charts
        setDeviceMetrics((prev) => {
          const existingMetrics = prev[device.id] || {};
          const metricData: Record<string, any[]> = { ...existingMetrics };

          // Process each historical metric record
          historicalMetrics.forEach((record: any) => {
            const metricsObj = record.metrics || {};
            Object.entries(metricsObj).forEach(([key, value]) => {
              if (typeof value === 'number') {
                if (!metricData[key]) {
                  metricData[key] = [];
                }
                metricData[key].push(value);
              }
            });
          });

          return { ...prev, [device.id]: metricData };
        });

        console.log(`Loaded ${historicalMetrics.length} historical metrics for device ${device.id}`);
      } catch (err) {
        console.error(`Failed to load historical metrics for device ${device.id}:`, err);
      }
    });
  }, [devices]);

  // Handle capture click
  const handleCaptureClick = (cameraDevice: any) => {
    if (!isDeviceSocketLive(cameraDevice)) return;
    setCaptureDeviceId(cameraDevice.id);
    setCaptureModalOpen(true);
  };

  return (
    <>
      {/* Sci-Fi Animation Styles */}
      <style>{`
        @keyframes scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .dashboard.blurred {
          filter: blur(4px);
          transition: filter 0.3s ease;
        }

        .status-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }

        .device-metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1rem;
        }

        @media (max-width: 1600px) {
          .device-metrics-grid {
            grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          }
        }

        @media (max-width: 1400px) {
          .device-metrics-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 1024px) {
          .status-cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          }
          .device-metrics-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .status-cards-grid {
            grid-template-columns: 1fr;
          }
          .device-metrics-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className={`dashboard ${mixerModalOpen ? 'blurred' : ''}`} style={{
        minHeight: '100vh',
        overflowY: 'auto',
        paddingBottom: '2rem'
      }}>
        <div className="heading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="text" style={{ flex: '1' }}>Welcome {user?.user?.fullname || user?.fullname || 'User'}</div>

          {/* View Toggle - Centered */}
          <div style={{
            display: 'flex',
            background: '#171a1c',
            borderRadius: '8px',
            border: '1px solid rgba(83, 189, 228, 0.3)',
            overflow: 'hidden',
          }}>
            <button
              onClick={() => handleViewChange("process")}
              style={{
                padding: '0.5rem 1rem',
                background: view === "process" ? 'rgba(83, 189, 228, 0.25)' : 'transparent',
                border: 'none',
                color: view === "process" ? '#53bde4' : '#6c757d',
                fontSize: '0.8rem',
                fontWeight: view === "process" ? 'bold' : 'normal',
                fontFamily: 'monospace',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.2s ease',
              }}
            >
              Process
            </button>
            <button
              onClick={() => handleViewChange("metrics")}
              style={{
                padding: '0.5rem 1rem',
                background: view === "metrics" ? 'rgba(83, 189, 228, 0.25)' : 'transparent',
                border: 'none',
                borderLeft: '1px solid rgba(83, 189, 228, 0.2)',
                color: view === "metrics" ? '#53bde4' : '#6c757d',
                fontSize: '0.8rem',
                fontWeight: view === "metrics" ? 'bold' : 'normal',
                fontFamily: 'monospace',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.2s ease',
              }}
            >
              Metrics
            </button>
            <button
              onClick={() => handleViewChange("network")}
              style={{
                padding: '0.5rem 1rem',
                background: view === "network" ? 'rgba(83, 189, 228, 0.25)' : 'transparent',
                border: 'none',
                borderLeft: '1px solid rgba(83, 189, 228, 0.2)',
                color: view === "network" ? '#53bde4' : '#6c757d',
                fontSize: '0.8rem',
                fontWeight: view === "network" ? 'bold' : 'normal',
                fontFamily: 'monospace',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.2s ease',
              }}
            >
              Network
            </button>
            <button
              onClick={() => handleViewChange("factory")}
              style={{
                padding: '0.5rem 1rem',
                background: view === "factory" ? 'rgba(83, 189, 228, 0.25)' : 'transparent',
                border: 'none',
                borderLeft: '1px solid rgba(83, 189, 228, 0.2)',
                color: view === "factory" ? '#53bde4' : '#6c757d',
                fontSize: '0.8rem',
                fontWeight: view === "factory" ? 'bold' : 'normal',
                fontFamily: 'monospace',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.2s ease',
              }}
            >
              Factory 3D
            </button>
          </div>

          {/* Right side actions */}
          <div className="actions" style={{ flex: '1', display: 'flex', justifyContent: 'flex-end' }}>
            <ActionButton icon={<TuneIcon />} />
          </div>
        </div>

        {view === "process" ? (
          <div style={{ padding: '0 1rem' }}>
            <BakeryProcessCanvas deviceMetrics={deviceMetrics} devices={devices || []} />
          </div>
        ) : view === "network" ? (
          <div style={{ padding: '0 1rem' }}>
            <NetworkGraph devices={devices || []} hostDevices={hostDevices} />
          </div>
        ) : view === "factory" ? (
          <div style={{ padding: '0 1rem', height: 'calc(100vh - 120px)' }}>
            <Factory3DViewer modelUrl="/models/factory.step" />
          </div>
        ) : (
          <div style={{ padding: '0 1rem' }}>
            {/* Status Cards */}
            <div className="status-cards-grid" style={{ marginBottom: '1rem' }}>
              {/* Online Devices Card */}
              <BasicInfoCard
                panelTitle={"Online Devices"}
                title={onlineDevices?.length || 0}
                sufix={"devices"}
              >
                <div style={{
                  marginTop: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {onlineDevices && onlineDevices.length > 0 ? (
                    onlineDevices.map((device, index) => (
                      <div key={device.id || index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        background: 'rgba(0, 212, 255, 0.05)',
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 212, 255, 0.2)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1rem' }}>📟</span>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#fff' }}>
                              {device.name || `Device ${device.id}`}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#aaa' }}>
                              {device.deviceTypes?.name || 'System Device'}
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#00ff88',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(0, 255, 136, 0.1)',
                          borderRadius: '4px'
                        }}>
                          ACTIVE
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                      No online devices
                    </div>
                  )}
                </div>
              </BasicInfoCard>

              {/* Offline Devices Card */}
              <BasicInfoCard
                panelTitle={"Offline Devices"}
                title={offlineDevices?.length || 0}
                sufix={"devices"}
              >
                <div style={{
                  marginTop: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {offlineDevices && offlineDevices.length > 0 ? (
                    offlineDevices.map((device, index) => (
                      <div key={device.id || index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        background: 'rgba(220, 53, 69, 0.05)',
                        borderRadius: '4px',
                        border: '1px solid rgba(220, 53, 69, 0.2)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1rem', opacity: 0.5 }}>📟</span>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#ccc' }}>
                              {device.name || `Device ${device.id}`}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#888' }}>
                              {device.deviceTypes?.name || 'System Device'}
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#dc3545',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(220, 53, 69, 0.1)',
                          borderRadius: '4px'
                        }}>
                          OFFLINE
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                      No offline devices
                    </div>
                  )}
                </div>
              </BasicInfoCard>

              {/* Workers Card */}
              <BasicInfoCard
                panelTitle={"Workers"}
                title={workers?.length || 0}
                sufix={"active"}
              >
                <div style={{
                  marginTop: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {workers && workers.length > 0 ? (
                    workers.map((worker, index) => (
                      <div key={worker.id || index} style={{
                        padding: '0.5rem',
                        background: worker.status === "Failed"
                          ? 'rgba(220, 53, 69, 0.05)'
                          : 'rgba(0, 255, 136, 0.05)',
                        borderRadius: '4px',
                        border: worker.status === "Failed"
                          ? '1px solid rgba(220, 53, 69, 0.2)'
                          : '1px solid rgba(0, 255, 136, 0.2)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            background: worker.status === "Failed"
                              ? 'rgba(220, 53, 69, 0.2)'
                              : 'rgba(0, 255, 136, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            flexShrink: 0
                          }}>
                            <WorkerIcon />
                          </div>

                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#fff' }}>
                              {worker.name}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#aaa' }}>
                              ID: {worker.id}
                            </div>
                          </div>
                        </div>

                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          color: worker.status === "Failed" ? '#dc3545' : '#00ff88',
                          marginBottom: '0.25rem'
                        }}>
                          {worker.status === "Failed"
                            ? `${worker.status}`
                            : `${worker.status} - ${worker.progress}%`}
                        </div>

                        <Line
                          percent={worker.progress}
                          strokeWidth={3}
                          strokeColor={worker.status === "Failed" ? "#dc3545" : "#00ff88"}
                          trailWidth={3}
                          trailColor="rgba(255, 255, 255, 0.1)"
                        />
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                      No active workers
                    </div>
                  )}
                </div>
              </BasicInfoCard>
            </div>

            {/* Group-based Device Cards */}
            <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {hostDevices.map((host: any) => {
                const group = host.groupId ? getDeviceGroup(host.groupId)?.deviceGroup : null;
                const childDevices = devicesByHost[host.id] || [];

                return (
                  <DashboardGroupCard
                    key={host.id}
                    group={group}
                    hostDevice={host}
                    childDevices={childDevices}
                    deviceMetrics={deviceMetrics}
                    onCaptureClick={handleCaptureClick}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mixer Modal */}
      {mixerModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease',
          }}
          onClick={() => setMixerModalOpen(false)}
        >
          <div
            style={{
              background: '#21282c',
              border: '2px solid #53bde4',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 0 60px rgba(83, 189, 228, 0.4)',
              position: 'relative',
              animation: 'scaleIn 0.3s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '2rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid rgba(83, 189, 228, 0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  fontSize: '2.5rem',
                  background: 'rgba(83, 189, 228, 0.2)',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(83, 189, 228, 0.4)',
                }}>
                  ⚙️
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    color: '#53bde4',
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    textShadow: '0 0 20px rgba(83, 189, 228, 0.5)',
                    letterSpacing: '1px',
                  }}>
                    MIXER CONTROL PANEL
                  </h2>
                  <p style={{
                    margin: '0.25rem 0 0 0',
                    color: '#6c757d',
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                  }}>
                    MECHANICAL ACTUATOR
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMixerModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  color: '#dc3545',
                  fontSize: '1.5rem',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>

            {/* Parameters Form */}
            <div style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              background: '#171a1c',
              border: '1px solid rgba(83, 189, 228, 0.3)',
              borderRadius: '12px',
            }}>
              <h3 style={{
                margin: '0 0 1.5rem 0',
                color: '#53bde4',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}>
                ⚙️ Process Parameters
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1.5rem',
              }}>
                {/* Temperature */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#53bde4',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                  }}>
                    Temperature (°C)
                  </label>
                  <input
                    type="number"
                    value={mixerFormData.temperature}
                    onChange={(e) => setMixerFormData({ ...mixerFormData, temperature: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#242424',
                      border: '2px solid rgba(83, 189, 228, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      outline: 'none',
                    }}
                  />
                </div>

                {/* Speed */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#53bde4',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                  }}>
                    Speed (RPM)
                  </label>
                  <input
                    type="number"
                    value={mixerFormData.speed}
                    onChange={(e) => setMixerFormData({ ...mixerFormData, speed: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#242424',
                      border: '2px solid rgba(83, 189, 228, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      outline: 'none',
                    }}
                  />
                </div>

                {/* Duration */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#53bde4',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                  }}>
                    Duration (min)
                  </label>
                  <input
                    type="number"
                    value={mixerFormData.duration}
                    onChange={(e) => setMixerFormData({ ...mixerFormData, duration: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#242424',
                      border: '2px solid rgba(83, 189, 228, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      outline: 'none',
                    }}
                  />
                </div>

                {/* Direction */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#53bde4',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                  }}>
                    Direction
                  </label>
                  <select
                    value={mixerFormData.direction}
                    onChange={(e) => setMixerFormData({ ...mixerFormData, direction: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#242424',
                      border: '2px solid rgba(83, 189, 228, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="clockwise">Clockwise</option>
                    <option value="counterclockwise">Counter-Clockwise</option>
                    <option value="alternate">Alternate</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setMixerModalOpen(false)}
                style={{
                  padding: '1rem 2rem',
                  background: 'transparent',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  color: '#6c757d',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('Running mixing procedure with params:', mixerFormData);
                  setMixerModalOpen(false);
                }}
                style={{
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, rgba(83, 189, 228, 0.4) 0%, rgba(83, 189, 228, 0.2) 100%)',
                  border: '2px solid #53bde4',
                  borderRadius: '8px',
                  color: '#53bde4',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  boxShadow: '0 0 20px rgba(83, 189, 228, 0.4)',
                }}
              >
                ▶ CONFIRM & RUN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capture Modal */}
      {captureModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease',
          }}
          onClick={() => !captureLoading && setCaptureModalOpen(false)}
        >
          <div
            style={{
              background: '#21282c',
              border: '2px solid #ff9800',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 0 60px rgba(255, 152, 0, 0.4)',
              position: 'relative',
              animation: 'scaleIn 0.3s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid rgba(255, 152, 0, 0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  fontSize: '2rem',
                  background: 'rgba(255, 152, 0, 0.2)',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 152, 0, 0.4)',
                }}>
                  {captureLoading ? '⏳' : '📷'}
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    color: '#ff9800',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                  }}>
                    {captureLoading ? 'CAPTURING...' : 'CAPTURE IMAGE'}
                  </h2>
                  <p style={{
                    margin: '0.25rem 0 0 0',
                    color: '#6c757d',
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                  }}>
                    {captureLoading ? 'Processing image...' : `QA Camera ${captureDeviceId ? `• Device ${captureDeviceId}` : ''}`}
                  </p>
                </div>
              </div>
              <button
                disabled={captureLoading}
                onClick={() => !captureLoading && setCaptureModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  color: '#dc3545',
                  fontSize: '1.5rem',
                  width: '40px',
                  height: '40px',
                  cursor: captureLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: captureLoading ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Name Input */}
            <div style={{ marginBottom: '1.5rem', display: captureLoading ? 'none' : 'block' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#ff9800',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
              }}>
                Capture Name
              </label>
              <input
                type="text"
                value={captureName}
                onChange={(e) => setCaptureName(e.target.value)}
                placeholder="Enter a name for this capture..."
                disabled={captureLoading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#242424',
                  border: '2px solid rgba(255, 152, 0, 0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontFamily: 'monospace',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                autoFocus
              />
            </div>

            {/* Buttons */}
            <div style={{
              display: captureLoading ? 'none' : 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
            }}>
              <button
                disabled={captureLoading}
                onClick={() => {
                  if (captureLoading) return;
                  setCaptureModalOpen(false);
                  setCaptureName('');
                  setCaptureDeviceId(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  color: '#6c757d',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: captureLoading ? 'not-allowed' : 'pointer',
                  opacity: captureLoading ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                disabled={captureLoading}
                onClick={async () => {
                  if (captureLoading) return;
                  setCaptureLoading(true);

                  try {
                    if (!captureDeviceId) {
                      alert('No camera device selected.');
                      setCaptureLoading(false);
                      return;
                    }
                    const captureDevice = devices?.find((d: any) => d.id === captureDeviceId);
                    const datasetId = captureDevice?.settings?.defaultDataset ?? 1;
                    const response: any = await cameraCapture(
                      captureDeviceId,
                      datasetId,
                      captureName || 'untitled'
                    );

                    const processedData = response?.processedImage ?? response?.image;
                    if (response && processedData) {
                      setCapturedProcessedImageData(processedData);
                      setCapturedOriginalImageData(response?.originalImage || '');
                      setCapturedMetrics(response.metrics ?? {});
                      setCaptureModalOpen(false);
                      setCaptureLoading(false);
                      setPreviewModalOpen(true);
                    } else {
                      alert('Failed to capture image. Please try again.');
                      setCaptureLoading(false);
                    }
                  } catch (error) {
                    console.error('Capture error:', error);
                    alert('Failed to capture image. Please try again.');
                    setCaptureLoading(false);
                  }
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.4) 0%, rgba(255, 152, 0, 0.2) 100%)',
                  border: '2px solid #ff9800',
                  borderRadius: '8px',
                  color: '#ff9800',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: captureLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 0 20px rgba(255, 152, 0, 0.4)',
                  opacity: captureLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                📷 CAPTURE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capture Preview Modal */}
      {previewModalOpen && (
        <CapturePreviewModal
          visible={previewModalOpen}
          imageData={capturedProcessedImageData}
          metrics={capturedMetrics}
          loading={confirmLoading}
          onConfirm={async () => {
            if (confirmLoading) return;
            setConfirmLoading(true);

            setTimeout(() => {
              setPreviewModalOpen(false);
              setCaptureName('');
              setCapturedProcessedImageData('');
              setCapturedOriginalImageData('');
              setCapturedMetrics({});
              setConfirmLoading(false);
              setCaptureDeviceId(null);
            }, 100);

            try {
              if (captureDeviceId) {
                const captureDevice = devices?.find((d: any) => d.id === captureDeviceId);
                const datasetId = captureDevice?.settings?.defaultDataset ?? 1;
                await confirmCapture(
                  capturedProcessedImageData,
                  capturedOriginalImageData,
                  capturedMetrics,
                  captureDeviceId,
                  datasetId,
                  captureName || 'untitled'
                );
                console.log('Image saved successfully');
              }
            } catch (error) {
              console.error('Failed to save image:', error);
            }
          }}
          onCancel={() => {
            setPreviewModalOpen(false);
            setCaptureName('');
            setCapturedProcessedImageData('');
            setCapturedOriginalImageData('');
            setCapturedMetrics({});
            setCaptureDeviceId(null);
          }}
        />
      )}
    </>
  );
};

export default Dashboard;
